import { useTranslations } from "next-intl";
import { useReducedMotion } from "framer-motion";
import {
  IconCalendarEvent,
  IconHelpCircle,
  IconPlus,
  IconSparkles,
  IconUsers,
  IconWand,
} from "@tabler/icons-react";
import { useUser } from "@/lib/query/hooks/useUser";
import type { BuilderSurface } from "@/lib/ai/ai-store";
import { VideoPlayerRiv } from "@/components/VideoPlayer/VideoPlayerRiv";
import { getStarterKeys, getBuilderStarterKeys } from "../drawerUtils";
import { AiCharacterRiv } from "../AiCharacterRiv";
import { AiOrb } from "../AiOrb";
import { AI_CHARACTER_ENABLED, AI_EMPTY_STATE_RIV } from "../aiCharacterConfig";

/**
 * Empty-transcript state. Chat: the Rive mascot (or orb under reduced-motion), a
 * personalised greeting, a scoped hint and a column of tappable starter cards.
 * Builder: the SAME mascot + the "Build with AI" hint + surface-specific starter
 * cards (email/document/website) — at parity with the chat experience.
 */

// Leading glyph per starter key — purely decorative, falls back to a sparkle.
const STARTER_ICONS: Record<string, typeof IconWand> = {
  event1: IconWand,
  event2: IconUsers,
  event3: IconHelpCircle,
  company1: IconCalendarEvent,
  company2: IconPlus,
  company3: IconHelpCircle,
};

export function EmptyState({
  variant,
  scopeKind,
  surface,
  onPick,
}: {
  variant: "chat" | "builder";
  scopeKind?: "event" | "company" | null;
  surface?: BuilderSurface | null;
  onPick?: (text: string) => void;
}) {
  const t = useTranslations("aiAssistant");
  const reduceMotion = useReducedMotion();
  // Hook order: called unconditionally before the builder early-return.
  const { data: user } = useUser();

  if (variant === "builder") {
    const builderStarters = getBuilderStarterKeys(surface ?? null);
    return (
      <div className="m-auto flex w-full flex-col items-center gap-3 px-4 py-6 text-center">
        {AI_CHARACTER_ENABLED ? (
          <AiCharacterRiv mode="idle" width="w-14" height="h-14" />
        ) : reduceMotion ? (
          // Autoplay scene can't honour reduced-motion — show the still orb.
          <AiOrb size="h-14 w-14" iconSize={26} />
        ) : (
          <VideoPlayerRiv videoName={AI_EMPTY_STATE_RIV} width="w-28" height="h-28" />
        )}
        <div className="flex flex-col gap-1">
          <div className="text-base font-medium">{t("builder.emptyTitle")}</div>
          <div className="text-sm font-tertiary">{t("builder.emptyHint")}</div>
        </div>
        {builderStarters.length > 0 && (
          <div className="mt-1 grid w-full max-w-80 grid-cols-1 gap-2">
            {builderStarters.map((key) => (
              <button
                key={key}
                type="button"
                className="press group flex items-start gap-2 rounded-xl border outline-quaternary surface-secondary hover:surface-tertiary px-3 py-2.5 text-left text-sm transition-colors cursor-pointer focus-ring"
                onClick={() => onPick?.(t(key))}
              >
                <IconWand size={16} className="mt-0.5 shrink-0 font-tertiary" />
                <span className="min-w-0 break-words">{t(key)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const name = user?.firstName?.trim();
  // `emptyTitleNamed` may not exist in every locale yet — guard and fall back
  // to the unnamed title (also covers the pre-load undefined-name frame).
  const greeting =
    name && t.has("emptyTitleNamed") ? t("emptyTitleNamed", { name }) : t("emptyTitle");
  const starters = getStarterKeys(scopeKind ?? null);

  return (
    <div className="m-auto flex w-full flex-col items-center gap-3 px-6 py-6 text-center">
      {AI_CHARACTER_ENABLED ? (
        <AiCharacterRiv mode="idle" width="w-16" height="h-16" />
      ) : reduceMotion ? (
        // Autoplay scene can't honour reduced-motion — show the still orb.
        <AiOrb size="h-16 w-16" iconSize={30} />
      ) : (
        <VideoPlayerRiv videoName={AI_EMPTY_STATE_RIV} width="w-36" height="h-36" />
      )}
      <div className="flex flex-col gap-1">
        <div className="text-base font-medium">{greeting}</div>
        <div className="text-sm font-tertiary">
          {scopeKind === "company" ? t("emptyHintCompany") : t("emptyHint")}
        </div>
      </div>
      <div className="mt-1 grid w-full max-w-80 grid-cols-1 gap-2">
        {starters.map((key) => {
          const Icon = STARTER_ICONS[key] ?? IconSparkles;
          return (
            <button
              key={key}
              type="button"
              className="press group flex items-start gap-2 rounded-xl border outline-quaternary surface-secondary hover:surface-tertiary px-3 py-2.5 text-left text-sm transition-colors cursor-pointer focus-ring"
              onClick={() => onPick?.(t(`starters.${key}`))}
            >
              <Icon size={16} className="mt-0.5 shrink-0 font-tertiary" />
              <span className="min-w-0 break-words">{t(`starters.${key}`)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
